import AxiosInstance from 'src/shared/services/axios/interceptor';

const controller = 'AccountingIntegration';

export interface AccountingIntegrationResponse {
  // eslint-disable-next-line camelcase
  accountingIntegration_id: number;
  // eslint-disable-next-line camelcase
  owner_id: number;
  provider: string;
  isActive: number;
  connectedByUser: string | null;
  createdAt: string | null;
  scheduleType: number | null;
  nextRun: string | null;
  fromDate: string | null;
  voucherGrouping: string | null;
}

export const getIntegrationsByOwnerId = (ownerId: number) => {
  return AxiosInstance.get<AccountingIntegrationResponse[]>(`${controller}/${ownerId}`);
};

export const getFortnoxAuthUrl = (ownerId: number) => {
  return AxiosInstance.get<{ url: string }>(`${controller}/fortnox/auth-url/${ownerId}`);
};

export const fortnoxCallback = (code: string, ownerId: number) => {
  return AxiosInstance.post(`${controller}/fortnox/callback`, { code, ownerId });
};

export const disconnectIntegration = (ownerId: number, provider: string) => {
  return AxiosInstance.put(`${controller}/${ownerId}/disconnect/${provider}`);
};

export const updateSchedule = (
  ownerId: number,
  provider: string,
  frequency: string,
  startDateTime?: string,
  fromDate?: string,
  voucherGrouping?: string,
) => {
  return AxiosInstance.put(`${controller}/${ownerId}/schedule/${provider}`, { frequency, startDateTime, fromDate, voucherGrouping });
};

export interface VoucherPreviewRow {
  account: string;
  accountName: string | null;
  accountExists: boolean;
  debit: number;
  credit: number;
  description: string;
  transactionInformation: string | null;
}

export interface VoucherPreview {
  description: string;
  transactionDate: string;
  voucherSeries: string;
  hasFinancialYear: boolean;
  paymentCount: number;
  totalAmount: number;
  rows: VoucherPreviewRow[];
}

export interface VoucherPreviewResponse {
  hasPayments: boolean;
  message?: string;
  vouchers?: VoucherPreview[];
  warnings?: string[];
  summary?: {
    voucherCount: number;
    paymentCount: number;
    totalAmount: number;
  };
}

export const getVoucherPreview = (ownerId: number, provider: string, grouping?: string, date?: string) => {
  const base = date ? `${controller}/${ownerId}/preview/${provider}/${date}` : `${controller}/${ownerId}/preview/${provider}`;
  const params = grouping ? `?grouping=${grouping}` : '';
  return AxiosInstance.get<VoucherPreviewResponse>(`${base}${params}`);
};

export interface FortnoxAccountValidation {
  accountNumber: string;
  accountName: string | null;
  exists: boolean;
}

export interface FortnoxItemValidation {
  value: string;
  description: string | null;
  valid: boolean;
}

export interface FortnoxValidationResult {
  success: boolean;
  companyName: string | null;
  accounts: FortnoxAccountValidation[];
  voucherSeries: FortnoxItemValidation;
  financialYear: FortnoxItemValidation;
}

export const validateIntegration = (ownerId: number, provider: string, date: string) => {
  return AxiosInstance.get<FortnoxValidationResult>(`${controller}/${ownerId}/validate/${provider}/${date}`);
};

export interface SendTestVoucherResponse {
  success: boolean;
  error?: string;
  verificationNo?: number;
  voucherCount?: number;
  vouchers?: { fortnoxVoucherNumber: number; fortnoxVoucherSeries: string; description: string }[];
  paymentCount?: number;
  totalAmount?: number;
}

export const sendTestVoucher = (ownerId: number, provider: string, grouping?: string, date?: string) => {
  const path = date ? `${controller}/${ownerId}/send-test/${provider}/${date}` : `${controller}/${ownerId}/send-test/${provider}`;
  const params = grouping ? `?grouping=${grouping}` : '';
  return AxiosInstance.post<SendTestVoucherResponse>(`${path}${params}`);
};

export interface FortnoxAccount {
  number: string;
  description: string;
}

export const getFortnoxAccounts = (ownerId: number, provider: string) => {
  return AxiosInstance.get<FortnoxAccount[]>(`${controller}/${ownerId}/accounts/${provider}`);
};
